import dayjs from 'dayjs';
import { environmentService } from '@dxlm/services';
import { TMethod, IHttpResponse } from '@dxlm/interfaces';

const ERROR_MESSAGE = 'Something went wrong. Please try again later.';

/**
 * @deprecated Please migrate over to `useHttpClient()` hook
 */
const POST = async <T>(url: string, body?: any, file?: File | File[]): Promise<IHttpResponse<T>> =>
    await execute('POST', url, null, body, file);

/**
 * @deprecated Please migrate over to `useHttpClient()` hook
 */
const PUT = async <T>(url: string, body?: any): Promise<IHttpResponse<T>> =>
    await execute('PUT', url, null, body);

/**
 * @deprecated Please migrate over to `useHttpClient()` hook
 */
const DELETE = async <T>(url: string, queryParams?: {[key: string]: any}): Promise<IHttpResponse<T>> => 
    await execute('DELETE', url, queryParams);

/**
 * @deprecated Please migrate over to `useHttpClient()` hook
 */
const GET = async <T>(url: string, queryParams?: {[key: string]: any}): Promise<IHttpResponse<T>> => 
    await execute('GET', url, queryParams);

/**
 * @deprecated Please migrate over to `useHttpClient()` hook
 */
const execute = async <T>(method: TMethod, url: string, queryParams?: {[key: string]: any}, body?: any, file?: File | File[]): Promise<IHttpResponse<T>> => {
    try {
        const fetchUrl = buildUrl(url, queryParams);
        const fetchOptions = buildFetchOptions(method, body, file);

        const fetchResponse = await fetch(fetchUrl, fetchOptions);
        if (fetchResponse.status === 401 || fetchResponse.status === 403) {
            if (window.location.pathname.startsWith('/sign-out')) {
                return null;
            }
            
            const queryParams: string[] = [];

            const searchParams = new URLSearchParams(window.location.search);
            if (!searchParams.get('reauth')) {
                searchParams.set('reauth', 'true');
            }
            if (!searchParams.get('r')) {
                const redirectUri = `${window.location.pathname}${window.location.search}`;
                searchParams.set('r', redirectUri);
            }
            Array.from(searchParams.keys()).forEach(x => {
                queryParams.push(`${x}=${encodeURIComponent(searchParams.get(x))}`);
            });

            const newUrl = `/sign-out/null?${queryParams.join('&')}`
            window.location.href = newUrl;
            return null;
        }

        const responseHeaders = Object.fromEntries(fetchResponse.headers.entries());
        if (responseHeaders['content-disposition'] && responseHeaders['content-disposition'].indexOf('attachment') >= 0) {
            return await downloadResponseAsFile(fetchResponse);
        }

        const response = await fetchResponse.json();
        if (fetchResponse.ok) {
            massageResponse(response);
        }
        
        return {
            success: fetchResponse.ok,
            statusCode: fetchResponse.status,
            data: response,
            errorMessages: fetchResponse.ok ? null : extractErrorMessages(response)
        };
    } catch (err: any) {
        console.error(err);
        return {
            statusCode: 500,
            success: false,
            errorMessages: [ERROR_MESSAGE]
        }
    }
}

const downloadResponseAsFile = async <T>(response: Response): Promise<IHttpResponse<T>> => {
    const responseHeaders = Object.fromEntries(response.headers.entries());
    const contentDispositionParts = responseHeaders['content-disposition'].split(';');
    const fileNameParts = contentDispositionParts[contentDispositionParts.length - 1].split('='); 

    const blobResponse = await response.blob();
    const url = window.URL.createObjectURL(blobResponse);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileNameParts[fileNameParts.length - 1];
    document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
    a.click();    
    a.remove();

    return {
        statusCode: 200,
        data: { message: 'File download will start shortly' } as T,
        errorMessages: [],
        success: true
    }
}

const isDate = (text: string): boolean =>
    text && typeof text === 'string' && text.indexOf('T') > -1 && !isNaN(parseInt(text.substring(0, 1))) && text.endsWith('Z') && dayjs(text).isValid()

const massageResponse = (response: any): any => {
    if (Array.isArray(response)) {
        for (let i = 0; i < response.length; i++) {
            response[i] = massageResponse(response[i]);
        }
    } else if (typeof response === 'object') {
        for (const key in response) {
            if (Array.isArray(response[key])) {
                for (let i = 0; i < response[key].length; i++) {
                    response[key][i] = massageResponse(response[key][i]);
                }
            } else if (typeof response[key] === 'object') {
                response[key] = massageResponse(response[key]);
            } else if (isDate(response[key])) {
                response[key] = dayjs(response[key]);
            }
        }
    } else if (isDate(response)) {
        return dayjs(response);
    }
    
    return response;
};

/**
 * @deprecated Please migrate over to `useHttpClient()` hook
 */
const buildUrl = (url: string, params?: {[key: string]: any}): string => {
    const queryString = buildQueryParams(params);
    return `${url.startsWith('http') ? '' : environmentService.getEnvironmentSettings().apiBaseUrl}${url}${queryString}`;
}

/**
 * @deprecated Please migrate over to `useHttpClient()` hook
 */
const buildQueryParams = (params: {[key: string]: any}): string => {
    if (!params) {
        return '';
    }

    const queryString = Object.keys(params)
        .filter(key => params[key] !== null && params[key] !== undefined && params[key] !== '')
        .map(key => {
            let paramString = params[key];
            if (typeof params[key] === 'object' && !Array.isArray(params[key])) {
                paramString = JSON.stringify(params[key]); // This will generally be Date objects, so stringify will convert to ISO date

                if (dayjs.isDayjs(params[key])) { // Check if this is a dayjs object
                    paramString = params[key].toISOString();
                } else if (dayjs(params[key]).isValid()) { // Check if this is a Date objet
                    paramString = dayjs(params[key]).toISOString();
                }
            } else if (Array.isArray(params[key])) {
                const paramStringParts: string[] = [];
                for (const item of params[key]) {
                    paramStringParts.push(`${key}=${encodeURIComponent(item)}`);
                }
                return paramStringParts.join('&');
            }
            return `${key}=${encodeURIComponent(paramString)}`;
        })
        .join('&');

    return `?${queryString}`;
};

const buildFetchOptions = (method: TMethod, body?: any, file?: File | File[]): RequestInit => {
    const baseHeaders = {
        'X-Timezone-Offset': `${new Date().getTimezoneOffset() * -1}`,
        'X-Timezone-Name': Intl.DateTimeFormat().resolvedOptions().timeZone,
        'X-App-Version': environmentService.getEnvironmentSettings().buildVersion,
        'X-Client-Name': 'dxm-lodgement-manager'
    };

    const options: RequestInit = {
        method,
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            ...baseHeaders
        }
    };

    if (file) {
        options.headers = {
            ...baseHeaders
        };
    }

    if (!file) {
        options.body = JSON.stringify(body);
    } else if (file) {
        const formData = new FormData();
        if (Array.isArray(file)) {
            for (const singleFile of file) {
                formData.append('file[]', singleFile);
            }
        } else {
            formData.append('file', file);
        }
        if (body) {
            const addFormEntry = (key: string, value: any) => {
                if (Array.isArray(value)) {
                    for (let i = 0; i < value.length; i++) {
                        const formKey = `${key}[${i}]`;
                        addFormEntry(formKey, value[i]);
                    }
                } else if (typeof value === 'object') {
                    for (const valueKey in value) {
                        const formKey = `${key}.${valueKey}`;
                        const formValue = value[valueKey];
                        if (typeof formValue === 'object') {
                            addFormEntry(formKey, formValue);
                        } else {
                            formData.append(formKey, formValue);
                        }
                    }
                } else {
                    formData.append(key, value);
                }
            }

            for (const key in body) {
                addFormEntry(key, body[key]);
            }
        }
        options.body = formData;
    }
    return options;
};

const addToFormData = (formData: FormData, data: any, previousKey?: string) => {
    if (data instanceof Object) {
        Object.keys(data).forEach(key => {
            const value = data[key];
            if (value instanceof Object && !Array.isArray(value)) {
                return addToFormData(formData, value, key);
            }
            if (previousKey) {
                key = `${previousKey}[${key}]`;
            }
            if (Array.isArray(value)) {
                value.forEach(val => {
                    formData.append(`${key}[]`, val);
                });
            } else {
                formData.append(key, value);
            }
        });
    }
}

const extractErrorMessages = (responseBody: any): string[] => {
    if (responseBody.errors && typeof responseBody.errors === 'object' && !Array.isArray(responseBody.errors) && Object.keys(responseBody.errors).length > 0) {
        const validErrors = Object.keys(responseBody.errors).filter(x => !x.startsWith('$'));
        if (validErrors.length > 0) {
            return validErrors.flatMap(key => responseBody.errors[key]);
        }
    } else if (responseBody.detail) {
        return [responseBody.detail];
    } else if (responseBody.message) {
        return [responseBody.message];
    }

    return [ERROR_MESSAGE];
};

const httpServiceWrapper = {
    buildQueryParams,
    buildUrl,
    execute,
    GET,
    POST,
    PUT,
    DELETE
};

export default httpServiceWrapper;